// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import { useDraftsToggle } from '../../controllers/drafts-toggle';
import { useConfluencePageTreeContext } from '../../context/page-tree/confluence-page-tree-context';
import { TranslationsProvider } from '../translations-provider';

const messages = defineMessages({
	label: {
		id: 'confluence-table-tree.drafts-toggle.label',
		defaultMessage: 'Show drafts',
		description: 'Label for the toggle that turns on or off showing drafts in the page tree',
	},
});
interface Props {
	locale: string;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	marginBottom: token('space.100', '8px'),
});

const DraftsToggle = () => {
	const { formatMessage } = useIntl();
	const [{ isDraftsShown, isTreeUpdatedForDrafts }, { setIsDraftsShown }] = useDraftsToggle();
	const { accountId } = useConfluencePageTreeContext();
	const handleChange = useCallback(
		(_: React.ChangeEvent, analyticsEvent: UIAnalyticsEvent) => {
			setIsDraftsShown(accountId, !isDraftsShown);
			fireUIAnalytics(analyticsEvent, 'showDraftsToggle', {
				isDraftsShown: !isDraftsShown,
			});
		},
		[accountId, isDraftsShown, setIsDraftsShown],
	);

	return (
		<Wrapper>
			<Toggle
				id="show-drafts"
				testId="show-drafts-toggle"
				isChecked={isDraftsShown || false}
				isDisabled={isDraftsShown === null || !isTreeUpdatedForDrafts}
				onChange={handleChange}
			/>
			<label htmlFor="show-drafts">{formatMessage(messages.label)}</label>
		</Wrapper>
	);
};

export default ({ locale }: Props) => (
	<TranslationsProvider locale={locale}>
		<DraftsToggle />
	</TranslationsProvider>
);
